import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';

import InteractionScreen from './screens/InteractionScreen';
import RNIBDashboard from './screens/SettingsPanel';
import ProfileSetup from './screens/ProfileSetup';
import Login from './screens/login';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import AdminDashboard from './screens/AdminDashboard';

const normalizeFontScale = (value) => {
  const allowed = new Set(['1', '1.1', '1.25', '1.4']);
  return allowed.has(String(value)) ? String(value) : '1';
};

const DashboardAutoOpener = () => {
  const location = useLocation();

  useEffect(() => {
    const sessionId = localStorage.getItem('authToken');
    const role = localStorage.getItem('role');

    if (sessionId && location.pathname === '/') {
      const dashboardPath = role?.toLowerCase() === 'admin' ? '/admin-dashboard' : '/dashboard';
      window.location.replace(dashboardPath);
    }
  }, [location.pathname]);

  return null;
};

export default function App() {
  const [selectedLanguage] = useState('en');
  const [profileData, setProfileData] = useState({});

  const [zoomLevel, setZoomLevel] = useState('1');
  const [trackingMode, setTrackingMode] = useState('cursor');
  const [fontScale, setFontScale] = useState(() =>
    normalizeFontScale(localStorage.getItem('fontScale') || '1')
  );
  const [highContrast, setHighContrast] = useState(
    () => localStorage.getItem('highContrast') === 'true'
  );
  const [invertColors, setInvertColors] = useState(false);
  const [colorBlindFilter, setColorBlindFilter] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const savedTheme = localStorage.getItem('theme');
    const themeMode = localStorage.getItem('themeMode');

    const applyTheme = (isDarkMode) => {
      document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    };

    if (themeMode === 'manual' && (savedTheme === 'light' || savedTheme === 'dark')) {
      document.documentElement.setAttribute('data-theme', savedTheme);
      return undefined;
    }

    applyTheme(mediaQuery.matches);
    localStorage.setItem('themeMode', 'auto');

    const handleThemeChange = (event) => {
      const latestThemeMode = localStorage.getItem('themeMode');
      if (latestThemeMode === 'manual') return;
      applyTheme(event.matches);
    };

    mediaQuery.addEventListener('change', handleThemeChange);
    return () => mediaQuery.removeEventListener('change', handleThemeChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const safeFontScale = normalizeFontScale(fontScale);
    if (safeFontScale !== fontScale) {
      setFontScale(safeFontScale);
      return;
    }

    root.setAttribute('data-font-scale', safeFontScale);
    root.setAttribute('data-contrast', highContrast ? 'high' : 'default');
    localStorage.setItem('fontScale', safeFontScale);
    localStorage.setItem('highContrast', String(highContrast));
  }, [fontScale, highContrast]);

  const handleProfile = (data) => {
    setProfileData(data);
  };

  return (
    <ThemeProvider>
      <Router>
        <DashboardAutoOpener />

        <Routes>
          {/* Login */}
          <Route
            path="/"
            element={
              localStorage.getItem('authToken') 
                ? localStorage.getItem('role')?.toLowerCase() === 'admin' 
                  ? <Navigate to="/admin-dashboard" replace /> 
                  : <Navigate to="/dashboard" replace />
                : <Login />
            }
          />

          {/* Interaction Screen - Protected */}
          <Route
            path="/interaction"
            element={
              <ProtectedRoute forbiddenRoles={['admin']}>
                <InteractionScreen language={selectedLanguage} onComplete={handleProfile} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          {/* Profile Setup - Protected */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute forbiddenRoles={['admin']}>
                <ProfileSetup
                  onFinish={(navigate) => navigate('/dashboard')}
                  profileData={profileData}
                />
              </ProtectedRoute>
            }
          />

          {/* Dashboard - Protected */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute forbiddenRoles={['admin']}>
                <RNIBDashboard
                  fontScale={fontScale}
                  setFontScale={setFontScale}
                  zoomLevel={zoomLevel}
                  setZoomLevel={setZoomLevel}
                  trackingMode={trackingMode}
                  setTrackingMode={setTrackingMode}
                  highContrast={highContrast}
                  setHighContrast={setHighContrast}
                  invertColors={invertColors}
                  setInvertColors={setInvertColors}
                  colorBlindFilter={colorBlindFilter}
                  setColorBlindFilter={setColorBlindFilter}
                />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}
